import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG } from '@boma/app-config';
import { IPaginatedResponse } from '@boma/client-shared';
import {
  IAccountingPlatformDetails,
  IExportCredentials,
  IExportCustomerResponse,
  IExportProductResponse,
  IExportSalesResponse,
  IProductAddToListResponse,
  ISyncProductListResponse,
} from '@boma/supplier-portal/shared/entities';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { IEntityAccountingCompany } from '../../../../entities/src/lib/definitions/i-entity-accounting-company';

@Injectable({
  providedIn: 'root',
})
export class IntegrationAccountingService {
  private readonly base: string;
  private readonly version: string;

  constructor(
    @Inject(APP_CONFIG) private configuration: any,
    private http: HttpClient
  ) {
    this.base = configuration.apiRoute;
    this.version = configuration.apiVersion;
  }

  createAccountingPlatform(
    entityId: string,
    companyId: string,
    username: string,
    password: string
  ) {
    return this.http.post<IAccountingPlatformDetails>(
      `${this.base}api/v${this.version}/Accounting/${entityId}/Platforms/Sage/${companyId}`,
      { username, password }
    );
  }

  setDefaultCashCustomer(
    entityId: string,
    accountingPlatformId: string,
    companyId: string,
    customerId: string
  ) {
    return this.http.put<IAccountingPlatformDetails>(
      `${this.base}api/v${this.version}/Accounting/${entityId}/Platforms/Sage/${companyId}/${accountingPlatformId}`,
      { customerId }
    );
  }

  setCredentials(
    entityId: string,
    accountingPlatformId: string,
    companyId: string,
    data: IExportCredentials
  ) {
    return this.http.put<IAccountingPlatformDetails>(
      `${this.base}api/v${this.version}/Accounting/${entityId}/Platforms/Sage/${companyId}/${accountingPlatformId}`,
      data
    );
  }

  getAccountingPlatforms(entityId: string) {
    return this.http.get<IAccountingPlatformDetails[]>(
      `${this.base}api/v${this.version}/Accounting/${entityId}/Platforms`
    );
  }

  createSageOneAccountingPlatform(
    request: { username: string; password: string },
    companyId: string,
    entityId: string
  ) {
    return this.http.post<IAccountingPlatformDetails>(
      `${this.base}api/v${this.version}/Accounting/${entityId}/Platforms/Sage/${companyId}`,
      request
    );
  }

  getAccountingPlatformDetails(entityId: string, accountingPlatformId: string) {
    return this.http.get<IAccountingPlatformDetails>(
      `${this.base}api/v${this.version}/Accounting/${entityId}/Platforms/${accountingPlatformId}`
    );
  }

  listAccountingCompanies(
    request: { username: string; password: string },
    entityId: string
  ) {
    const { password, username } = request;
    return this.http.get<{ id: string; name: string }[]>(
      `${this.base}api/v${this.version}/Accounting/${entityId}/Companies?username=${username}&password=${encodeURIComponent(password)}`
    );
  }

  //  PRODUCT EXPORT
  //  -------------------------------------------------------------------------------------------------------------
  //  PRODUCTS CAN BE ADDED/REMOVED TO A "SYNC LIST" SO THAT SUPPLIERS HAVE CONTROL OVER WHICH PRODUCTS ARE EXPORTED

  // SPECIFY AN ARRAY OF PRODUCT IDS TO EXPORT WITHOUT HAVING TO BUILD A SYNC LIST
  exportProductArrayToPlatform(
    entityId: string,
    platformId: string,
    companyId: string,
    productIds: string[]
  ) {
    return this.http
      .post<IExportProductResponse[]>(
        `${this.base}api/v${this.version}/Accounting/${entityId}/Products/Export/${companyId}/${platformId}`,
        { productIds }
      )
      .pipe(
        switchMap((res) => {
          const hasError = res.filter((r) => !!r.failureReason);
          if (hasError.length)
            throw Error(
              `One or more products failed to be exported: ${res[0].failureReason}`
            );

          return of(res);
        })
      );
  }

  exportProductSyncListToPlatform(
    entityId: string,
    companyId: string,
    entityAccountingPlatformId: string
  ) {
    return this.http.post<any>(
      `${this.base}api/v${this.version}/Accounting/${entityId}/Products/List/Export/${entityAccountingPlatformId}/${companyId}`,
      {}
    );
  }

  // EXPORT THE CREATED SYNC LIST
  addProductToSyncList(
    entityId: string,
    entityAccountingPlatformId: string,
    productIds: string[]
  ) {
    console.log(productIds);
    return this.http.post<IProductAddToListResponse>(
      `${this.base}api/v${this.version}/Accounting/${entityId}/Products/Add?entityAccountingPlatformId=${entityAccountingPlatformId}`,
      productIds
    );
  }

  removeProductFromSyncList(
    entityId: string,
    entityAccountingPlatformId: string,
    productIds: string[]
  ) {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      body: productIds,
    };

    return this.http.delete<any>(
      `${this.base}api/v${this.version}/Accounting/${entityId}/Products/Remove?entityAccountingPlatformId=${entityAccountingPlatformId}`,
      options
    );
  }

  getProductSyncList(
    entityId: string,
    entityAccountingPlatformId: string,
    productIds: string[],
    page: string = '1',
    perPage: string = '10'
  ) {
    return this.http.post<IPaginatedResponse<ISyncProductListResponse>>(
      `${this.base}api/v${this.version}/Accounting/${entityId}/Products/List?entityAccountingPlatformId=${entityAccountingPlatformId}&page=${page}&pageSize=${perPage}`,
      productIds
    );
  }

  //  CUSTOMER
  //  --------------------------------------------------------------------------------------------------------------

  exportCustomerToPlatform(
    entityId: string,
    entityAccountingPlatformId: string,
    conflictStrategy: string = 'Source', // Source, Destination, Newest
    customerId: string
  ) {
    return this.http.post<any>(
      `${this.base}api/v${this.version}/Accounting/${entityId}/Customers/Export/${customerId}?entityAccountingPlatformId=${entityAccountingPlatformId}&conflictStrategy=${conflictStrategy}`,
      {}
    );
  }

  getCustomerSyncList(
    entityId: string,
    entityAccountingPlatformId: string,
    query: string = '',
    customerIds: string[] = [],
    page: string = '1',
    perPage: string = '25'
  ) {
    let urlQuery = '';

    if (query.length > 0) {
      urlQuery = `&query=${query}`;
    }

    return this.http.post<IPaginatedResponse<IExportCustomerResponse>>(
      `${this.base}api/v${this.version}/Accounting/${entityId}/Customers/List?entityAccountingPlatformId=${entityAccountingPlatformId}&page=${page}&pageSize=${perPage}${urlQuery}`,
      customerIds
    );
  }

  addCustomerToSyncList(
    entityId: string,
    entityAccountingPlatformId: string,
    customerIds: string[]
  ) {
    return this.http.post<IExportCustomerResponse[]>(
      `${this.base}api/v${this.version}/Accounting/${entityId}/Customers/Add?entityAccountingPlatformId=${entityAccountingPlatformId}`,
      customerIds
    );
  }

  addAllCustomersToExportList(
    entityId: string,
    entityAccountingPlatformId: string
  ) {
    return this.http.post<IExportCustomerResponse[]>(
      `${this.base}api/v${this.version}/Accounting/${entityId}/Customers/AddAll?entityAccountingPlatformId=${entityAccountingPlatformId}`,
      {}
    );
  }

  removeCustomerFromSyncList(
    entityId: string,
    entityAccountingPlatformId: string,
    customerIds: string[]
  ) {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      body: customerIds,
    };

    return this.http.delete<any>(
      `${this.base}api/v${this.version}/Accounting/${entityId}/Customers/Remove?entityAccountingPlatformId=${entityAccountingPlatformId}`,
      options
    );
  }

  exportCustomerArrayToPlatform(
    entityId: string,
    entityAccountingPlatformId: string,
    conflictStrategy: string = 'Source', // Source, Destination, Newest
    customerIds: string[]
  ) {
    console.log(customerIds);
    return this.http.post<any>(
      `${this.base}api/v${this.version}/Accounting/${entityId}/Customers/Export/?entityAccountingPlatformId=${entityAccountingPlatformId}&conflictStrategy=${conflictStrategy}`,
      customerIds
    );
  }

  exportCustomerSyncListToAccountingPlatform(
    entityId: string,
    entityAccountingPlatformId: string,
    conflictStrategy: string
  ) {
    return this.http.post<any>(
      `${this.base}api/v${this.version}/Accounting/${entityId}/Customers/List/Export/?entityAccountingPlatformId=${entityAccountingPlatformId}&conflictStrategy=${conflictStrategy}`,
      {}
    );
  }

  // SALES

  exportSales(
    entityId: string,
    entityAccountingPlatformId: string,
    companyId: string,
    salesIds: string[]
  ) {
    return this.http.post<IPaginatedResponse<IExportSalesResponse>>(
      `${this.base}api/v${this.version}/Accounting/${entityId}/Sales/Export?entityAccountingPlatformId=${entityAccountingPlatformId}&companyId=${companyId}`,
      salesIds
    );
  }

  // EXPORT THE CREATED SYNC LIST

  testSync(entityId: string) {
    return this.http.post<any>(
      `${this.base}api/v${this.version}/Accounting/${entityId}/SyncTest`,
      {}
    );
  }

  // GET USERS SAGE COMPANIES
  // getSageCompaniesForUser(credentials: { username: string; password: string }) {
  //   return this.http.get<any>(
  //     `${this.sageUrl}/Company/Get?apikey=${this.sageApiKey}`,
  //     credentials
  //   );
  // }

  getSageCompanies(
    entityId: string,
    credentials: { username: string; password: string }
  ) {
    return this.http.get<IEntityAccountingCompany[]>(
      `${this.base}api/v${this.version}/Accounting/${entityId}/Companies`,
      {
        params: {
          username: credentials.username,
          password: credentials.password,
        },
      }
    );
  }
}
